import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  CircularProgress,
  Alert,
  Link,
  Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AddIcon from '@mui/icons-material/Add';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';

import api from '../api';
import EditCustomerDialog from '../components/EditCustomerDialog';
import SuspendActivateCustomerDialog from '../components/SuspendActivateCustomerDialog';
import AddCustomerDialog from '../components/AddCustomerDialog';

interface Customer {
  tenantID: string;
  companyName: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  subscriptionType: string;
  enterpriseClientLicense?: string;
  clientLicenseSubscriptionId?: string;
  enterpriseClientLicenseTermUnit?: string;
  enterpriseClientLicenseStartDate?: string;
  enterpriseClientLicenseEndDate?: string;
  enterpriseClientLicenseAutoRenew?: boolean;
  enterpriseClientLicenseExpired?: boolean;
  isActive: boolean;
  createdDate: string;
  lastLoginDate?: string;
  totalDeployments: number;
  activePolicies: number;
}

interface CustomersResponse {
  customers: Customer[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export default function Customers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [customerToChangeStatus, setCustomerToChangeStatus] = useState<Customer | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: (page + 1).toString(),
        pageSize: rowsPerPage.toString()
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      if (statusFilter !== 'all') {
        params.append('status', statusFilter === 'active' ? 'Active' : 'Suspended');
      }

      const response = await api.get<CustomersResponse>(`/TCAdminCustomers?${params}`);

      setCustomers(response.data.customers);
      setTotalCount(response.data.totalCount);
    } catch (err: any) {
      setError(err.response?.data || 'Failed to load customers');
      console.error('Error fetching customers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [page, rowsPerPage, searchTerm, statusFilter]);

  const handleEditClick = (customer: Customer) => {
    setSelectedCustomer(customer);
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedCustomer(null);
  };

  const handleEditSave = () => {
    fetchCustomers(); // Refresh the list after successful update
  };

  const handleStatusClick = (customer: Customer) => {
    setCustomerToChangeStatus(customer);
    setStatusDialogOpen(true);
  };

  const handleStatusDialogClose = () => {
    setStatusDialogOpen(false);
    setCustomerToChangeStatus(null);
  };

  const handleStatusSuccess = () => {
    fetchCustomers(); // Refresh the list after successful status change // 
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (event: any) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const hasAttachedEnterpriseLicense = (customer: Customer) =>
    (customer.enterpriseClientLicense ?? '').toLowerCase() === 'yes';

  const formatAttachedLicenseTerm = (customer: Customer) => {
    if (!customer.enterpriseClientLicenseStartDate && !customer.enterpriseClientLicenseEndDate) {
      return null;
    }

    const start = customer.enterpriseClientLicenseStartDate
      ? new Date(customer.enterpriseClientLicenseStartDate).toLocaleDateString()
      : 'Unknown start';
    const end = customer.enterpriseClientLicenseEndDate
      ? new Date(customer.enterpriseClientLicenseEndDate).toLocaleDateString()
      : 'No end date';
    const unit = customer.enterpriseClientLicenseTermUnit
      ? ` (${customer.enterpriseClientLicenseTermUnit})`
      : '';

    return `${start} - ${end}${unit}`;
  };
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [customerToReset, setCustomerToReset] = useState<Customer | null>(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState<string | null>(null);
  const handleResetClick = (customer: Customer) => {
    setCustomerToReset(customer);
    setResetDialogOpen(true);
    setResetError(null);
    setResetSuccess(null);
  };

  const handleResetDialogClose = () => {
    setResetDialogOpen(false);
    setCustomerToReset(null);
    setResetLoading(false);
    setResetError(null);
    setResetSuccess(null);
  };

  const handleResetTenant = async () => {
    if (!customerToReset) return;
    setResetLoading(true);
    setResetError(null);
    setResetSuccess(null);
    try {
      await api.post(`/TCAdminCustomers/${customerToReset.tenantID}/reset`);
      setResetSuccess('Tenant reset complete.');
      fetchCustomers();
    } catch (err: any) {
      setResetError(err.response?.data || 'Failed to reset tenant');
    } finally {
      setResetLoading(false);
    }
  };
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Customer Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          Add Customer
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search by company name or email..."
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ flexGrow: 1, minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} onChange={handleStatusFilterChange} label="Status">
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Company Name</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Subscription</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Deployments</TableCell>
              <TableCell align="center">Policies</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : customers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">
                    No customers found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              customers.map((customer) => (
                <TableRow key={customer.tenantID} hover>
                  <TableCell>
                    <Link
                      component="button"
                      variant="body2"
                      fontWeight="medium"
                      onClick={() => navigate(`/customers/${customer.tenantID}`)}
                      sx={{ textAlign: 'left', cursor: 'pointer' }}
                    >
                      {customer.companyName}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{customer.contactName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {customer.contactEmail}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-start' }}>
                      <Chip label={customer.subscriptionType} size="small" color="primary" variant="outlined" />
                      {hasAttachedEnterpriseLicense(customer) && (
                        <Chip
                          label={customer.enterpriseClientLicenseExpired ? 'Attached License Expired' : 'Attached License Active'}
                          size="small"
                          color={customer.enterpriseClientLicenseExpired ? 'error' : 'success'}
                          variant="outlined"
                        />
                      )}
                      {hasAttachedEnterpriseLicense(customer) && formatAttachedLicenseTerm(customer) && (
                        <Typography variant="caption" color="text.secondary">
                          {formatAttachedLicenseTerm(customer)}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip
                        label={customer.isActive ? 'Active' : 'Suspended'}
                        size="small"
                        color={customer.isActive ? 'success' : 'error'}
                        icon={customer.isActive ? <CheckCircleIcon /> : <BlockIcon />}
                        onClick={() => handleStatusClick(customer)}
                        sx={{ cursor: 'pointer' }}
                      />
                      <Tooltip title={customer.isActive ? 'Click to suspend this customer' : 'Click to activate this customer'}>
                        <InfoOutlinedIcon sx={{ fontSize: 16, color: 'info.main' }} />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell align="center">{customer.totalDeployments}</TableCell>
                  <TableCell align="center">{customer.activePolicies}</TableCell>
                  <TableCell>{formatDate(customer.lastLoginDate)}</TableCell>
                  <TableCell>{formatDate(customer.createdDate)}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <Tooltip title="Edit Customer">
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleEditClick(customer)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Gap Assessment">
                        <IconButton 
                          size="small" 
                          color="secondary"
                          onClick={() => navigate(`/customers/${customer.tenantID}/gap-assessment`)}
                        >
                          <AssessmentIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reset Tenant">
                        <span>
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleResetClick(customer)}
                            disabled={resetLoading}
                          >
                            <RestartAltIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                      {/* Reset Tenant Confirmation Dialog */}
                      <Dialog open={resetDialogOpen} onClose={handleResetDialogClose}>
                        <DialogTitle>Reset Tenant</DialogTitle>
                        <DialogContent>
                          {resetError && <Alert severity="error" sx={{ mb: 2 }}>{resetError}</Alert>}
                          {resetSuccess && <Alert severity="success" sx={{ mb: 2 }}>{resetSuccess}</Alert>}
                          <DialogContentText>
                            {customerToReset && (
                              <>
                                Are you sure you want to reset the tenant <b>{customerToReset.companyName}</b>?<br />
                                <b>This will delete all groups and policies for this tenant.</b><br />
                                This action cannot be undone.
                              </>
                            )}
                          </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                          <Button onClick={handleResetDialogClose} disabled={resetLoading}>Cancel</Button>
                          <Button onClick={handleResetTenant} color="warning" disabled={resetLoading || !!resetSuccess}>
                            {resetLoading ? <CircularProgress size={20} /> : 'Reset Tenant'}
                          </Button>
                        </DialogActions>
                      </Dialog>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </TableContainer>

      <EditCustomerDialog
        open={editDialogOpen}
        customer={selectedCustomer}
        onClose={handleEditDialogClose}
        onSave={handleEditSave}
      />

      <AddCustomerDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={() => {
          fetchCustomers();
        }}
      />

      <SuspendActivateCustomerDialog
        open={statusDialogOpen}
        customer={customerToChangeStatus}
        onClose={handleStatusDialogClose}
        onSuccess={handleStatusSuccess}
      />
    </Box>
  );
}
